import React from "react";
import { Box, Typography, Checkbox, Radio, TextField, Chip, useTheme, Tooltip } from "@mui/material";
import { AccessReviewStatusChip } from "../../../../../components/AccessReviewStatusChip/AccessReviewStatusChip";
import { ReviewDecisionStatus, AssessmentStatus } from "../../../../../utils/enums";

export const UserTableCell = ({
  row,
  column,
  decisions,
  commentsByUser,
  selectedUsers,
  isDisabled,
  isCompletedOrRejected = false,
  isSystemArchived = false,
  isDraftAndNotOwner = false,
  onDecisionChange,
  onCommentChange,
  onUserSelection,
}) => {
  const theme = useTheme();
  const isUserSelected = selectedUsers.includes(row.id);
  const isBulkMode = selectedUsers.length >= 2;
  const isActionDisabled = isDisabled || (isBulkMode && isUserSelected);
  const current = decisions[row.id] || ReviewDecisionStatus.APPROVED;
  const showComment = !(isBulkMode && isUserSelected) && (current === ReviewDecisionStatus.MODIFIED || current === ReviewDecisionStatus.REJECTED);

  if (column.field === "checkbox") {
    if (isCompletedOrRejected) {
      return null;
    }
    return (
      <Tooltip title={isSystemArchived ? "System is archived. Activate it to start a review." : isDraftAndNotOwner ? "You are not an owner of the system" : ""} arrow>
        <span>
      <Checkbox
        checked={selectedUsers.includes(row.id)}
        onChange={() => onUserSelection(row.id)}
        disabled={isDisabled}
        sx={{
          p: 0,
          color: "#9CA3AF",
          "&.Mui-checked": {
            color: "#3B82F6",
          },
              "&.Mui-disabled": {
                color: "#D1D5DB",
              },
        }}
      />
        </span>
      </Tooltip>
    );
  }

  if (column.field === "user_name") {
    return (
      <Box display="flex" alignItems="center">
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.4,
            color: "text.primary",
          }}
        >
          {row.name || "Unknown User"}
        </Typography>
      </Box>
    );
  }

  if (column.field === "user_email") {
    return (
      <Typography
        variant="body2"
        sx={{ color: theme.customColors?.text?.secondary || "#6B7280" }}
      >
        {row.email || "-"}
      </Typography>
    );
  }

  if (column.field === "role") {
    const roleText = row.role || "No Role";
    return (
      <Tooltip title={roleText} arrow placement="top">
        <Chip
          label={roleText}
          size="medium"
          sx={{
            borderRadius: "0px",
            backgroundColor: "#1976d214",
            color: "#1976d2",
            fontWeight: 500,
            fontSize: "0.75rem",
            maxWidth: "100%",
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
      </Tooltip>
    );
  }

  if (column.field === "review_status") {
    if (isCompletedOrRejected && row.assessmentStatus === AssessmentStatus.REJECTED) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: theme.customColors?.text?.secondary || "#6B7280",
            textAlign: "center",
          }}
        >
          -
        </Typography>
      );
    }
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <AccessReviewStatusChip status={row.review_status || ReviewDecisionStatus.APPROVED} />
      </Box>
    );
  }

  if (column.field === "review_notes") {
    if (isCompletedOrRejected && row.assessmentStatus === AssessmentStatus.REJECTED) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: theme.customColors?.text?.secondary || "#6B7280",
            textAlign: "center",
          }}
        >
          -
        </Typography>
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{
          color: theme.customColors?.text?.secondary || "#6B7280",
          textAlign: "center",
          fontStyle: row.review_notes ? "normal" : "italic",
        }}
      >
        {row.review_notes || "-"}
      </Typography>
    );
  }

  if (column.field === "actions") {
    if (isDisabled && !isCompletedOrRejected) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: theme.customColors?.text?.secondary || "#6B7280",
            textAlign: "center",
            fontStyle: "italic",
          }}
        >
          -
        </Typography>
      );
    }
    
    return (
      <Box sx={{ opacity: isActionDisabled ? 0.5 : 1, pointerEvents: isActionDisabled ? "none" : "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            justifyContent: "center",
            mb: showComment ? 2 : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Radio
              checked={current === ReviewDecisionStatus.APPROVED}
              onChange={() => onDecisionChange(row.id, ReviewDecisionStatus.APPROVED)}
              disabled={isActionDisabled}
              sx={{
                p: 0,
                m: 0,
                color: current === ReviewDecisionStatus.APPROVED ? "#16A34A" : "#9CA3AF",
                "&.Mui-checked": { color: "#16A34A" },
                "&:hover": { bgcolor: "transparent" },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: current === ReviewDecisionStatus.APPROVED ? "#16A34A" : "#6B7280",
                opacity: isActionDisabled ? 0.6 : 1,
              }}
            >
              Approve
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Radio
              checked={current === ReviewDecisionStatus.MODIFIED}
              onChange={() => onDecisionChange(row.id, ReviewDecisionStatus.MODIFIED)}
              disabled={isActionDisabled}
              sx={{
                p: 0,
                m: 0,
                color: current === ReviewDecisionStatus.MODIFIED ? "#F59E0B" : "#9CA3AF",
                "&.Mui-checked": { color: "#F59E0B" },
                "&:hover": { bgcolor: "transparent" },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: current === ReviewDecisionStatus.MODIFIED ? "#F59E0B" : "#6B7280",
                opacity: isActionDisabled ? 0.6 : 1,
              }}
            >
              Modify
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Radio
              checked={current === ReviewDecisionStatus.REJECTED}
              onChange={() => onDecisionChange(row.id, ReviewDecisionStatus.REJECTED)}
              disabled={isActionDisabled}
              sx={{
                p: 0,
                m: 0,
                color: current === ReviewDecisionStatus.REJECTED ? "#DC2626" : "#9CA3AF",
                "&.Mui-checked": { color: "#DC2626" },
                "&:hover": { bgcolor: "transparent" },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: current === ReviewDecisionStatus.REJECTED ? "#DC2626" : "#6B7280",
                opacity: isActionDisabled ? 0.6 : 1,
              }}
            >
              Revoke
            </Typography>
          </Box>
        </Box>

        {showComment && (
          <TextField
            size="small"
            placeholder={
              current === ReviewDecisionStatus.MODIFIED
                ? "What's the new role and why?"
                : "Reason for revoking"
            }
            value={commentsByUser[row.id] || ""}
            onChange={(e) => onCommentChange(row.id, e.target.value)}
            disabled={isActionDisabled}
            fullWidth
            multiline
            rows={2}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
                "& fieldset": {
                  borderColor: theme.customColors?.border?.main || "#E5E7EB",
                },
                "&:hover fieldset": {
                  borderColor: isActionDisabled ? "#E5E7EB" : theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        )}
      </Box>
    );
  }

  return <Typography variant="body2">{row[column.field] || "-"}</Typography>;
};

